/**
 * 🎯 RealtimeProvider v2.0 - инициализирует AK-47 Realtime Hub на уровне приложения
 * 
 * Обеспечивает:
 * - Единую точку инициализации realtime подписок
 * - Автоматическое переподключение при смене отеля
 * - Доступ к broadcast функциям через контекст
 * - ⭐ НОВОЕ: Presence - отслеживание онлайн админов
 * - ⭐ НОВОЕ: Регистрация callback обработчиков
 */

import React, { createContext, useContext, ReactNode, useMemo } from 'react';
import { 
  useRealtimeHub, 
  RealtimeTable, 
  AdminPresence,
  NotificationHandler,
  SyncProgressHandler
} from '@/hooks/useRealtimeHub';

interface RealtimeContextValue {
  // Состояние подключения
  isConnected: boolean;
  subscribedTables: RealtimeTable[];
  lastUpdate: number;
  
  // Broadcast функции
  broadcastChange: (table: RealtimeTable, eventType: string, entityId?: string) => void;
  broadcastBookingChange: (eventType: string, bookingId: string) => void;
  broadcastFinancialChange: (eventType: string, bookingId: string) => void;
  broadcastSyncProgress: (progressData: any) => void;
  
  // ⭐ НОВОЕ: Presence данные и функции
  onlineAdmins: Map<string, AdminPresence>;
  trackBookingEdit: (bookingId: string) => Promise<void>;
  untrackBookingEdit: () => Promise<void>;
  updateCurrentPage: (page: string) => Promise<void>;
  
  // ⭐ НОВОЕ: Регистрация обработчиков
  registerNotificationHandler: (handler: NotificationHandler) => string;
  unregisterNotificationHandler: (id: string) => void;
  registerSyncProgressHandler: (handler: SyncProgressHandler) => string;
  unregisterSyncProgressHandler: (id: string) => void;
}

const RealtimeContext = createContext<RealtimeContextValue | null>(null);

interface RealtimeProviderProps {
  children: ReactNode;
}

export const RealtimeProvider: React.FC<RealtimeProviderProps> = ({ children }) => {
  // Инициализируем Hub с уведомлениями только на уровне провайдера
  const hubState = useRealtimeHub({ 
    showNotifications: true,
    debug: false 
  });

  const value = useMemo<RealtimeContextValue>(() => ({
    // Состояние
    isConnected: hubState.isConnected,
    subscribedTables: hubState.subscribedTables,
    lastUpdate: hubState.lastUpdate,
    
    // Broadcast
    broadcastChange: hubState.broadcastChange,
    broadcastBookingChange: hubState.broadcastBookingChange,
    broadcastFinancialChange: hubState.broadcastFinancialChange,
    broadcastSyncProgress: hubState.broadcastSyncProgress,
    
    // Presence ⭐ НОВОЕ
    onlineAdmins: hubState.onlineAdmins,
    trackBookingEdit: hubState.trackBookingEdit,
    untrackBookingEdit: hubState.untrackBookingEdit,
    updateCurrentPage: hubState.updateCurrentPage,
    
    // Регистрация обработчиков ⭐ НОВОЕ
    registerNotificationHandler: hubState.registerNotificationHandler,
    unregisterNotificationHandler: hubState.unregisterNotificationHandler,
    registerSyncProgressHandler: hubState.registerSyncProgressHandler,
    unregisterSyncProgressHandler: hubState.unregisterSyncProgressHandler
  }), [hubState]);

  return (
    <RealtimeContext.Provider value={value}>
      {children}
    </RealtimeContext.Provider>
  );
};

/**
 * Хук для доступа к Realtime контексту
 */
// 🚀 ОПТИМИЗАЦИЯ: Fallback объект вынесен за пределы компонента для стабильной ссылки
const FALLBACK_CONTEXT: RealtimeContextValue = {
  isConnected: false,
  subscribedTables: [],
  lastUpdate: 0,
  broadcastChange: () => {},
  broadcastBookingChange: () => {},
  broadcastFinancialChange: () => {},
  broadcastSyncProgress: () => {},
  onlineAdmins: new Map(),
  trackBookingEdit: async () => {},
  untrackBookingEdit: async () => {},
  updateCurrentPage: async () => {},
  registerNotificationHandler: () => '',
  unregisterNotificationHandler: () => {},
  registerSyncProgressHandler: () => '',
  unregisterSyncProgressHandler: () => {}
};

export const useRealtime = (): RealtimeContextValue => {
  const context = useContext(RealtimeContext);
  
  // 🚀 Убран console.warn - экономим CPU в production
  return context ?? FALLBACK_CONTEXT;
};

export default RealtimeContext;
